import React, { createContext, useContext, useReducer, useEffect } from 'react';
import axios from 'axios';
import { setToken, clearToken, loadToken, getToken } from '../utils/tokenStorage';
// NOTE: emailService removed - all emails are sent from backend only for security

// Apply withCredentials globally so every axios request (including direct calls in
// other service files) automatically sends the httpOnly auth cookie.
axios.defaults.withCredentials = true;

// Create AuthContext
const AuthContext = createContext();

// Initial state
const initialState = {
    user: null,
    token: null,
    isLoading: true,
    isAuthenticated: false,
    // True only once the session is confirmed server-side (login/register/auth-me),
    // NOT for the optimistic localStorage hydration. Gating decisions wait for this
    // so a stale cached approval_status can't briefly mis-route the user.
    validated: false,
    error: null
};

// Action types
const AUTH_ACTIONS = {
    LOGIN_START: 'LOGIN_START',
    LOGIN_SUCCESS: 'LOGIN_SUCCESS',
    LOGIN_FAILURE: 'LOGIN_FAILURE',
    REGISTER_START: 'REGISTER_START',
    REGISTER_SUCCESS: 'REGISTER_SUCCESS',
    REGISTER_FAILURE: 'REGISTER_FAILURE',
    LOGOUT: 'LOGOUT',
    LOAD_USER_START: 'LOAD_USER_START',
    LOAD_USER_SUCCESS: 'LOAD_USER_SUCCESS',
    LOAD_USER_FAILURE: 'LOAD_USER_FAILURE',
    CLEAR_ERROR: 'CLEAR_ERROR',
    UPDATE_USER: 'UPDATE_USER'
};

// Reducer
const authReducer = (state, action) => {
    switch (action.type) {
        case AUTH_ACTIONS.LOGIN_START:
        case AUTH_ACTIONS.REGISTER_START:
        case AUTH_ACTIONS.LOAD_USER_START:
            return {
                ...state,
                isLoading: true,
                error: null
            };

        case AUTH_ACTIONS.LOGIN_SUCCESS:
        case AUTH_ACTIONS.REGISTER_SUCCESS:
            return {
                ...state,
                isLoading: false,
                isAuthenticated: true,
                validated: true, // came straight from a server response
                user: action.payload.user,
                token: action.payload.token,
                error: null
            };

        case AUTH_ACTIONS.LOAD_USER_SUCCESS:
            return {
                ...state,
                isLoading: false,
                isAuthenticated: true,
                // Optimistic localStorage hydration passes validated:false; the
                // /auth/me confirmation passes validated:true.
                validated: action.payload.validated === true ? true : state.validated,
                user: action.payload.user,
                token: action.payload.token || state.token, // Preserve token
                error: null
            };

        case AUTH_ACTIONS.LOGIN_FAILURE:
        case AUTH_ACTIONS.REGISTER_FAILURE:
        case AUTH_ACTIONS.LOAD_USER_FAILURE:
            return {
                ...state,
                isLoading: false,
                isAuthenticated: false,
                validated: false,
                user: null,
                token: null,
                error: action.payload
            };

        case AUTH_ACTIONS.LOGOUT:
            return {
                ...state,
                isLoading: false,
                isAuthenticated: false,
                validated: false,
                user: null,
                token: null,
                error: null
            };

        case AUTH_ACTIONS.CLEAR_ERROR:
            return {
                ...state,
                error: null
            };

        case AUTH_ACTIONS.UPDATE_USER:
            return {
                ...state,
                user: { ...state.user, ...action.payload }
            };

        default:
            return state;
    }
};

// API Configuration
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// Create axios instance with interceptors
// Accepts optional logout callback to handle 401 Unauthorized responses.
// Token is passed for backward-compat with in-memory state; the httpOnly cookie
// is the primary auth mechanism and is sent automatically via withCredentials.
const createApiClient = (token, onUnauthorized = null) => {
    const client = axios.create({
        baseURL: API_BASE_URL,
        timeout: 10000,
        withCredentials: true, // Send httpOnly cookie on every request
        headers: {
            'Content-Type': 'application/json'
        }
    });

    // Request interceptor: add Authorization header if token is in memory,
    // and extend timeout for AI endpoints.
    client.interceptors.request.use(
        (config) => {
            if (token) {
                // In-memory token used as Authorization header (fallback for API clients
                // and for requests before the cookie propagates after login).
                config.headers.Authorization = `Bearer ${token}`;
            }
            // Increase timeout for AI endpoints (they take longer)
            if (config.url && config.url.includes('/ai/')) {
                config.timeout = 120000; // 120 seconds for AI requests
            }
            return config;
        },
        (error) => Promise.reject(error)
    );

    // Response interceptor to handle errors
    client.interceptors.response.use(
        (response) => response,
        (error) => {
            // Handle 401 Unauthorized - automatically log out user
            if (error.response?.status === 401) {
                // Prevent infinite loops: don't logout if already on login page or if it's a logout request
                const isLoginPage = window.location.pathname === '/login' || window.location.pathname === '/register';
                const isLogoutRequest = error.config?.url?.includes('/auth/logout');
                
                if (!isLoginPage && !isLogoutRequest && onUnauthorized) {
                    console.warn('401 Unauthorized - Logging out user automatically');
                    // Call logout callback to clear auth state and redirect
                    onUnauthorized();
                }
            }
            return Promise.reject(error);
        }
    );

    return client;
};

// AuthProvider component
export const AuthProvider = ({ children }) => {
    const [state, dispatch] = useReducer(authReducer, initialState);

    // Initialize auth on mount
    useEffect(() => {
        initializeAuth();
    }, []);

    // Initialize authentication on app load.
    // Strategy: show cached user profile instantly for fast UI, then validate
    // the session server-side via the httpOnly cookie. Token is NOT read from
    // localStorage — cookie is the sole persistent auth mechanism.
    const initializeAuth = async () => {
        try {
            // On native, hydrate token from secure storage so the Bearer
            // interceptor can attach it to the /auth/me validation call.
            const persistedToken = await loadToken();

            const storedUser = localStorage.getItem('eqaya_user');

            if (storedUser) {
                // Hydrate UI immediately from cached profile (non-sensitive data)
                const parsedUser = JSON.parse(storedUser);
                dispatch({
                    type: AUTH_ACTIONS.LOAD_USER_SUCCESS,
                    payload: { user: parsedUser, token: persistedToken, validated: false }
                });
            }

            // Verify session server-side. On web the httpOnly cookie carries auth;
            // on native the Bearer token (loaded above) handles it.
            const apiClient = createApiClient(persistedToken, null);
            const response = await apiClient.get('/auth/me');

            if (response.data.success) {
                // Refresh the cached profile so the next optimistic hydration isn't stale.
                try { localStorage.setItem('eqaya_user', JSON.stringify(response.data.data.user)); } catch (_) { /* non-fatal */ }
                dispatch({
                    type: AUTH_ACTIONS.LOAD_USER_SUCCESS,
                    payload: { user: response.data.data.user, token: persistedToken, validated: true }
                });

                // Auto-capture the browser's IANA timezone (fire-and-forget,
                // once per detected zone) — powers "It's 6:17 AM for {name}"
                // in Messages between diaspora clients and local contractors.
                try {
                    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
                    if (tz && localStorage.getItem('eqaya_tz_sent') !== tz) {
                        apiClient.put('/auth/timezone', { timezone: tz })
                            .then(() => { try { localStorage.setItem('eqaya_tz_sent', tz); } catch (_) {} })
                            .catch(() => { /* non-fatal — retried next app load */ });
                    }
                } catch (_) { /* Intl unavailable — skip */ }
            } else {
                await clearToken();
                localStorage.removeItem('eqaya_user');
                dispatch({ type: AUTH_ACTIONS.LOAD_USER_FAILURE, payload: 'Session invalid' });
            }
        } catch (error) {
            // 401 or network error — clear cached profile and token
            await clearToken();
            localStorage.removeItem('eqaya_user');
            dispatch({ type: AUTH_ACTIONS.LOAD_USER_FAILURE, payload: 'No active session' });
        }
    };

    // Login function
    const login = async (email, password, recaptchaToken) => {
        dispatch({ type: AUTH_ACTIONS.LOGIN_START });

        try {
            const apiClient = createApiClient();
            const response = await apiClient.post('/auth/login', {
                email,
                password,
                ...(recaptchaToken && { recaptchaToken })
            });

            if (response.data.success) {
                const { user, token } = response.data.data;

                // Store user profile for fast UI hydration on next load.
                localStorage.setItem('eqaya_user', JSON.stringify(user));

                // Persist token: in-memory on web, secure storage on native.
                await setToken(token);

                dispatch({
                    type: AUTH_ACTIONS.LOGIN_SUCCESS,
                    payload: { user, token }
                });

                return { success: true, user };
            } else {
                throw new Error(response.data.error || 'Login failed');
            }
        } catch (error) {
            const errorMessage = error.response?.data?.error || error.message || 'Login failed';
            dispatch({
                type: AUTH_ACTIONS.LOGIN_FAILURE,
                payload: errorMessage
            });
            return { success: false, error: errorMessage };
        }
    };

    // Google login function
    const loginWithGoogle = async (idToken, role = 'client') => {
        dispatch({ type: AUTH_ACTIONS.LOGIN_START });

        try {
            const apiClient = createApiClient();
            const response = await apiClient.post('/auth/google', {
                idToken,
                role
            });

            if (response.data.success) {
                const { user, token } = response.data.data;

                localStorage.setItem('eqaya_user', JSON.stringify(user));
                await setToken(token);

                dispatch({
                    type: AUTH_ACTIONS.LOGIN_SUCCESS,
                    payload: { user, token }
                });

                return { success: true, user };
            } else {
                throw new Error(response.data.error || 'Google login failed');
            }
        } catch (error) {
            const errorMessage = error.response?.data?.error || error.message || 'Google login failed';
            dispatch({
                type: AUTH_ACTIONS.LOGIN_FAILURE,
                payload: errorMessage
            });
            return { success: false, error: errorMessage };
        }
    };

    // Register function
    const register = async (userData, recaptchaToken) => {
        dispatch({ type: AUTH_ACTIONS.REGISTER_START });

        try {
            const apiClient = createApiClient();
            let acquisition = null;
            try { acquisition = require('../utils/acquisition').getAcquisition(); } catch (_) { /* optional */ }
            const response = await apiClient.post('/auth/register', {
                ...userData,
                ...(acquisition && { acquisition }),
                ...(recaptchaToken && { recaptchaToken })
            });

            if (response.data.success) {
                const { user, verificationEmailSent, inviteDetails } = response.data.data;

                dispatch({
                    type: AUTH_ACTIONS.REGISTER_SUCCESS,
                    payload: { user, token: null } // Registration doesn't automatically log in
                });

                // NOTE: All emails (welcome, verification) are sent automatically from the backend
                // Never trigger emails from the client side for security reasons

                return {
                    success: true,
                    user,
                    verificationEmailSent: !!verificationEmailSent,
                    inviteDetails: inviteDetails || null,
                    message: response.data.message || 'Registration successful! Please check your email to verify your account.'
                };
            } else {
                throw new Error(response.data.error || 'Registration failed');
            }
        } catch (error) {
            const errorMessage = error.response?.data?.error || error.message || 'Registration failed';
            dispatch({
                type: AUTH_ACTIONS.REGISTER_FAILURE,
                payload: errorMessage
            });
            return { success: false, error: errorMessage };
        }
    };

    // Email verification function
    const verifyEmail = async (userId, code) => {
        try {
            const apiClient = createApiClient();
            const response = await apiClient.post('/auth/verify-email', {
                userId,
                code
            });

            if (response.data.success) {
                // Auto-login after email verification
                if (response.data.data && response.data.data.token) {
                    const { token, user } = response.data.data;

                    localStorage.setItem('eqaya_user', JSON.stringify(user));
                    await setToken(token);

                    dispatch({
                        type: AUTH_ACTIONS.LOGIN_SUCCESS,
                        payload: { token, user }
                    });
                }
                
                return { 
                    success: true, 
                    message: response.data.message,
                    token: response.data.data?.token,
                    user: response.data.data?.user
                };
            } else {
                throw new Error(response.data.error || 'Email verification failed');
            }
        } catch (error) {
            const errorMessage = error.response?.data?.error || error.message || 'Email verification failed';
            return { success: false, error: errorMessage };
        }
    };

    // Logout function
    const logout = async () => {
        try {
            // Call logout endpoint regardless of in-memory token state — the server
            // needs to invalidate the session and clear the httpOnly cookie.
            // Use state.token if available (Authorization header), otherwise rely on cookie.
            const apiClient = createApiClient(state.token || null, null);
            await apiClient.post('/auth/logout');
        } catch (error) {
            // Swallow errors — local cleanup happens regardless
            console.error('Logout error:', error);
        } finally {
            // Always clear local state, user profile cache, and token
            await clearToken();
            localStorage.removeItem('eqaya_user');
            dispatch({ type: AUTH_ACTIONS.LOGOUT });
        }
    };

    // Forgot password function
    const forgotPassword = async (email, recaptchaToken) => {
        try {
            const apiClient = createApiClient();
            const response = await apiClient.post('/auth/forgot-password', {
                email,
                ...(recaptchaToken && { recaptchaToken })
            });

            if (response.data.success) {
                return {
                    success: true,
                    message: response.data.message
                };
            } else {
                throw new Error(response.data.error || 'Password reset request failed');
            }
        } catch (error) {
            const errorMessage = error.response?.data?.error || error.message || 'Password reset request failed';
            return { success: false, error: errorMessage };
        }
    };

    // Reset password function
    const resetPassword = async (email, code, newPassword) => {
        try {
            const apiClient = createApiClient();
            const response = await apiClient.post('/auth/reset-password', {
                email,
                code,
                newPassword
            });

            if (response.data.success) {
                return { success: true, message: response.data.message };
            } else {
                throw new Error(response.data.error || 'Password reset failed');
            }
        } catch (error) {
            const errorMessage = error.response?.data?.error || error.message || 'Password reset failed';
            return { success: false, error: errorMessage };
        }
    };

    // Update user profile function
    const updateUser = (userData) => {
        dispatch({
            type: AUTH_ACTIONS.UPDATE_USER,
            payload: userData
        });

        // Update localStorage
        const updatedUser = { ...state.user, ...userData };
        localStorage.setItem('eqaya_user', JSON.stringify(updatedUser));
    };

    // Clear error function
    const clearError = () => {
        dispatch({ type: AUTH_ACTIONS.CLEAR_ERROR });
    };

    // Check if user has specific role
    const hasRole = (role) => {
        return state.user?.role === role;
    };

    // Check if user is client
    const isClient = () => hasRole('client');

    // Check if user is contractor
    const isContractor = () => hasRole('contractor');

    // Check if user is admin
    const isAdmin = () => hasRole('admin');

    // Check if user is verifier
    const isVerifier = () => hasRole('verifier');

    // Check if user is quality inspector
    const isQualityInspector = () => hasRole('quality_inspector');

    // Check if user is any type of inspector/verifier
    const isInspector = () => isVerifier() || isQualityInspector();

    // Get API client with current token and auto-logout on 401
    const getApiClient = () => {
        return createApiClient(state.token, () => {
            // Handle 401 Unauthorized - logout and redirect to login
            handleUnauthorized();
        });
    };

    // Handle 401 Unauthorized response
    const handleUnauthorized = () => {
        // Clear auth state: token from memory/native storage, user profile from cache
        clearToken(); // fire-and-forget — redirect follows immediately
        localStorage.removeItem('eqaya_user');
        dispatch({ type: AUTH_ACTIONS.LOGOUT });
        
        // Redirect to login page
        // Use window.location to ensure full page reload and clear any cached state
        const currentPath = window.location.pathname;
        if (currentPath !== '/login' && currentPath !== '/register') {
            window.location.href = '/login?session=expired';
        }
    };

    const approvalStatus = state.user?.approvalStatus || state.user?.approval_status || null;
    const isTestAccount = state.user?.isTest === true || state.user?.is_test === true;
    const isApprovedContractor = state.user?.role !== 'contractor' || approvalStatus === 'approved' || isTestAccount;

    const value = {
        // State
        user: state.user,
        token: state.token,
        isLoading: state.isLoading,
        isAuthenticated: state.isAuthenticated,
        // True once the session is confirmed server-side. Gating waits for this so
        // a stale optimistic cache can't briefly mis-route the user.
        sessionValidated: state.validated,
        error: state.error,

        // Functions
        login,
        loginWithGoogle,
        register,
        logout,
        verifyEmail,
        forgotPassword,
        resetPassword,
        updateUser,
        refreshAuth: initializeAuth,
        clearError,
        hasRole,
        isClient,
        isContractor,
        isAdmin,
        isVerifier,
        isQualityInspector,
        isInspector,
        getApiClient,
        approvalStatus,
        isTestAccount,
        isApprovedContractor
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};

// Custom hook to use AuthContext
export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

// Higher-order component for protected routes
export const withAuth = (Component, requiredRole = null) => {
    return function AuthenticatedComponent(props) {
        const { isAuthenticated, isLoading, user } = useAuth();

        if (isLoading) {
            return <div>Loading...</div>;
        }

        if (!isAuthenticated) {
            // Redirect to login page
            window.location.href = '/login';
            return null;
        }

        if (requiredRole && user?.role !== requiredRole) {
            return <div>Access denied. Required role: {requiredRole}</div>;
        }

        return <Component {...props} />;
    };
};

export default AuthContext;
